import type { ClipboardEvent, FC, KeyboardEvent, ReactNode, RefObject } from 'react'

import type { ChatAttachment } from './chat-input-attachment.interface'
import { ChatInputAttachmentArea } from './chat-input-attachment-area.molecule'
import { ChatInputHiddenFile } from './chat-input-hidden-file.atom'
import type { ChatInputMode } from './chat-input-mode.type'
import { ChatInputTextarea } from './chat-input-textarea.atom'
import { ChatInputToolbar } from './chat-input-toolbar.molecule'
import type { PromptTemplate } from './prompt-template.interface'

export interface ChatInputComposerBodyProps {
  value: string
  mode: ChatInputMode
  attachmentList: ChatAttachment[]
  placeholder: string
  maxHeight: number
  isDisabled: boolean
  isFileAttachEnabled: boolean
  isLoading: boolean
  isUploading: boolean
  uploadProgress: number
  isNoteMode: boolean
  canSend: boolean
  textareaRef: RefObject<HTMLTextAreaElement | null>
  fileInputRef: RefObject<HTMLInputElement | null>
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  handleKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void
  handlePaste: (e: ClipboardEvent<HTMLTextAreaElement>) => void
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleRemoveAttachment: (id: string) => void
  handleModeChange: (mode: ChatInputMode) => void
  handleAttachFromComputer: () => void
  handleAttachFromChats: () => void
  handleSend: () => void
  templateList?: PromptTemplate[]
  handleTemplateSelect?: (templateId: string) => void
  handleCreateTemplate?: () => void
  isStreaming?: boolean
  isStopping?: boolean
  onStop?: () => void
}

/**
 * Body of the chat input composer containing file input, attachments, textarea, and toolbar.
 */
export const ChatInputComposerBody: FC<ChatInputComposerBodyProps> = (p): ReactNode => (
  <>
    <ChatInputHiddenFile ref={p.fileInputRef} onChange={p.handleFileInputChange} />
    {(p.attachmentList.length > 0 || p.isUploading) && (
      <div className="border-b px-3 pt-3">
        <ChatInputAttachmentArea
          attachmentList={p.attachmentList}
          onRemoveAttachment={p.handleRemoveAttachment}
          isUploading={p.isUploading}
          uploadProgress={p.uploadProgress}
        />
      </div>
    )}
    <ChatInputTextarea
      ref={p.textareaRef}
      value={p.value}
      placeholder={p.placeholder}
      maxHeight={p.maxHeight}
      isDisabled={p.isDisabled}
      onChange={p.handleChange}
      onKeyDown={p.handleKeyDown}
      onPaste={p.handlePaste}
    />
    <div className="border-t px-2 py-2">
      <ChatInputToolbar
        mode={p.mode}
        onModeChange={p.handleModeChange}
        onAttachFromComputer={p.handleAttachFromComputer}
        onAttachFromChats={p.handleAttachFromChats}
        onSend={p.handleSend}
        canSend={p.canSend}
        isLoading={p.isLoading}
        isDisabled={p.isDisabled}
        isFileAttachEnabled={p.isFileAttachEnabled}
        isNoteMode={p.isNoteMode}
        isStreaming={p.isStreaming}
        isStopping={p.isStopping}
        onStop={p.onStop}
        templateList={p.templateList}
        onTemplateSelect={p.handleTemplateSelect}
        onCreateTemplate={p.handleCreateTemplate}
      />
    </div>
  </>
)

ChatInputComposerBody.displayName = 'ChatInputComposerBody'
